// Section 18 and 10: Tool allocation policy. Who gets tools when there are fewer tools than
// workers: farmers first, highest output first, builders first, even rotation, or manual pins
// (phases/shared/tools.ts applies it at the start of the season). The preview names tools on
// hand against the workers who want one and who the option puts first.
// Default: config.policies.defaults.toolAllocation.
import type { Config } from '../../config';
import { noChangeCost, noProblem } from '../../core/content-helpers';
import { isWorking, peopleSorted } from '../../core/queries';
import { definePolicy, noModifiers } from '../../core/registry';
import type { PreviewLine } from '../../core/types';

export function toolAllocation(config: Config) {
  return definePolicy({
    id: 'toolAllocation',
    label: 'Tool allocation',
    section: 'S18',
    group: 'work',
    options: ['farmers-first', 'highest-output', 'builders-first', 'even-rotation', 'manual'],
    default: config.policies.defaults.toolAllocation,
    modifierTargets: [],
    modifiers: noModifiers,
    changeCost: noChangeCost,
    affects(ctx, option) {
      const wanting = peopleSorted(ctx.state).filter(
        (p) => p.job !== 'rest' && isWorking(p) && ctx.registry.jobs[p.job].toolFactor !== null,
      );
      const tools = Math.floor(ctx.state.stocks.tools.amount + 1e-9);
      const lines: PreviewLine[] = [
        {
          key: 'policy.toolAllocation.stock',
          params: { tools, workers: wanting.length, short: Math.max(0, wanting.length - tools) },
        },
      ];
      if (wanting.length <= tools && option !== 'manual') return lines;
      const count = (job: string): number =>
        Math.min(tools, wanting.filter((p) => p.job === job).length);
      switch (option) {
        case 'farmers-first':
          lines.push({
            key: `policy.toolAllocation.${option}`,
            params: { count: count('farmer') },
          });
          break;
        case 'builders-first':
          lines.push({
            key: `policy.toolAllocation.${option}`,
            params: { count: count('builder') },
          });
          break;
        case 'manual':
          lines.push({
            key: `policy.toolAllocation.${option}`,
            params: {
              count: Math.min(
                tools,
                wanting.filter((p) => ctx.state.toolOverrides[p.id] === true).length,
              ),
            },
          });
          break;
        case 'even-rotation':
        case 'highest-output':
          lines.push({ key: `policy.toolAllocation.${option}`, params: {} });
          break;
      }
      return lines;
    },
    validate: noProblem,
  });
}
